/**
 * Medication list screen — shows all active patient medications.
 * Uses pill images and drug purposes for a warm, informative display.
 */

import type { PatientMedication } from '../../types/index';
import { getPatientMedications, type PatientMedRecord } from '../../core/db';
import { getDrugById } from '../../core/drug-store';
import { t } from '../../i18n';

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function toPatientMed(rec: PatientMedRecord): PatientMedication {
  return {
    id: rec.id,
    drug_rxcui: rec.drug_rxcui,
    drug_name: rec.drug_name,
    strength: rec.strength,
    frequency: rec.frequency as PatientMedication['frequency'],
    times: rec.times,
    instructions: rec.instructions,
    active: rec.active === 1,
  };
}

function frequencyLabel(freq: string): string {
  const keyMap: Record<string, string> = {
    once_daily: 'medList.frequency.once',
    twice_daily: 'medList.frequency.twice',
    three_daily: 'medList.frequency.thrice',
    four_daily: 'medList.frequency.four',
    every_other_day: 'medList.frequency.everyOther',
    weekly: 'medList.frequency.weekly',
    as_needed: 'medList.frequency.asNeeded',
  };
  const key = keyMap[freq];
  return key ? t(key) : freq;
}

function formatTime12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

const PILL_IMAGE_MAP: Record<string, string> = {
  'metformin': '/pill-images/metformin-500.svg',
  'lisinopril': '/pill-images/lisinopril-10.svg',
  'atorvastatin': '/pill-images/atorvastatin-20.svg',
  'omeprazole': '/pill-images/omeprazole-20.svg',
};

function getPillImage(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, path] of Object.entries(PILL_IMAGE_MAP)) {
    if (lower.includes(key)) return path;
  }
  return '/pill-images/default-pill.svg';
}

const DRUG_PURPOSE_KEY_MAP: Record<string, string> = {
  'metformin': 'purpose.bloodSugar',
  'lisinopril': 'purpose.bloodPressure',
  'atorvastatin': 'purpose.cholesterol',
  'omeprazole': 'purpose.stomachAcid',
  'amlodipine': 'purpose.bloodPressure',
  'losartan': 'purpose.bloodPressure',
  'gabapentin': 'purpose.nervePain',
  'sertraline': 'purpose.moodAnxiety',
  'levothyroxine': 'purpose.thyroid',
  'aspirin': 'purpose.heartProtection',
  'warfarin': 'purpose.bloodClots',
  'furosemide': 'purpose.fluidRemoval',
  'prednisone': 'purpose.inflammation',
  'insulin': 'purpose.bloodSugar',
};

function getDrugPurpose(drugName: string): string {
  const lower = drugName.toLowerCase();
  for (const [key, purposeKey] of Object.entries(DRUG_PURPOSE_KEY_MAP)) {
    if (lower.includes(key)) return t(purposeKey);
  }
  return '';
}

// -----------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------

export async function renderMedListScreen(container: HTMLElement): Promise<void> {
  container.textContent = '';
  container.className = 'screen';

  const wrapper = document.createElement('div');
  wrapper.className = 'flex-col gap-md';
  wrapper.style.cssText = 'max-width: 600px; margin: 0 auto;';

  const header = document.createElement('header');
  header.className = 'screen-header';
  const title = document.createElement('h2');
  title.textContent = t('medList.title');
  header.appendChild(title);
  wrapper.appendChild(header);

  const meds = getPatientMedications().map(toPatientMed);

  if (meds.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'card text-center';
    empty.style.padding = 'var(--space-2xl) var(--space-lg)';

    const emptyText = document.createElement('p');
    emptyText.className = 'text-muted';
    emptyText.textContent = t('medList.empty');
    emptyText.style.fontSize = 'var(--font-size-large)';
    empty.appendChild(emptyText);

    const emptyHint = document.createElement('p');
    emptyHint.className = 'text-dim';
    emptyHint.textContent = t('medList.emptyHint');
    empty.appendChild(emptyHint);

    wrapper.appendChild(empty);
  } else {
    const list = document.createElement('div');
    list.className = 'flex-col gap-sm';
    list.setAttribute('role', 'list');
    list.setAttribute('aria-label', t('medList.activeLabel'));

    for (const med of meds) {
      const item = document.createElement('div');
      item.className = 'card card-interactive';
      item.setAttribute('role', 'listitem');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label',
        med.drug_name + ', ' + med.strength + ', ' + frequencyLabel(med.frequency));
      item.style.cssText = 'display: flex; align-items: center; gap: var(--space-md); padding: var(--space-md) var(--space-lg); border-left: 4px solid var(--color-primary);';

      // Pill image instead of color dot
      const imgWrapper = document.createElement('div');
      imgWrapper.style.cssText = 'flex-shrink: 0; width: 56px; height: 56px; border-radius: 12px; background: var(--color-primary-light); display: flex; align-items: center; justify-content: center; overflow: hidden;';

      const img = document.createElement('img');
      img.src = getPillImage(med.drug_name);
      img.alt = med.drug_name + ' pill';
      img.style.cssText = 'width: 40px; height: 40px; object-fit: contain;';
      img.onerror = () => { img.onerror = null; img.src = '/pill-images/default-pill.svg'; };
      imgWrapper.appendChild(img);
      item.appendChild(imgWrapper);

      const textBlock = document.createElement('div');
      textBlock.className = 'flex-col';
      textBlock.style.cssText = 'flex: 1; min-width: 0; gap: 2px;';

      const nameEl = document.createElement('div');
      nameEl.style.cssText = 'font-size: var(--font-size-large); font-weight: 700; color: var(--color-text);';
      nameEl.textContent = med.drug_name.toLowerCase().includes(med.strength.toLowerCase())
        ? med.drug_name
        : med.drug_name + ' ' + med.strength;
      textBlock.appendChild(nameEl);

      // Drug purpose
      const purpose = getDrugPurpose(med.drug_name);
      if (purpose) {
        const purposeEl = document.createElement('div');
        purposeEl.style.cssText = 'font-size: 14px; color: var(--color-primary); font-weight: 500;';
        purposeEl.textContent = purpose;
        textBlock.appendChild(purposeEl);
      }

      const detailRow = document.createElement('div');
      detailRow.style.cssText = 'display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 14px; color: var(--color-text-muted);';

      const freqEl = document.createElement('span');
      freqEl.textContent = frequencyLabel(med.frequency);
      detailRow.appendChild(freqEl);

      const dot = document.createElement('span');
      dot.className = 'text-dim';
      dot.setAttribute('aria-hidden', 'true');
      dot.textContent = '\u00B7';
      detailRow.appendChild(dot);

      const timesEl = document.createElement('span');
      timesEl.textContent = med.times.map(formatTime12h).join(' & ');
      detailRow.appendChild(timesEl);

      textBlock.appendChild(detailRow);
      item.appendChild(textBlock);

      const chevron = document.createElement('span');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.textContent = '\u203A';
      chevron.style.cssText = 'font-size: var(--font-size-xl); color: var(--color-text-dim);';
      item.appendChild(chevron);

      const navigate = (): void => {
        window.location.hash = '#drug/' + med.drug_rxcui;
      };
      item.addEventListener('click', navigate);
      item.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(); }
      });

      list.appendChild(item);
    }

    wrapper.appendChild(list);
  }

  const addBtn = document.createElement('button');
  addBtn.className = 'btn btn-primary btn-lg btn-block mt-lg';
  addBtn.textContent = t('medList.addMedication');
  addBtn.setAttribute('aria-label', t('medList.addLabel'));
  addBtn.addEventListener('click', () => { window.location.hash = '#setup'; });
  wrapper.appendChild(addBtn);

  container.appendChild(wrapper);
}
